import React, { FC, useRef, useState } from 'react';
import translate from '@translate';
import ProductsSelect from 'src/components/ProductsSelect';
import { checkFormValidity } from 'src/utils/formFunctions';
import { OrderProductType } from 'src/types/order';
import TextField from 'src/components/TextField';
import Grid, { GridItem } from 'src/components/Grid';
import Button from 'src/components/Button';
import Typography from 'src/components/Typography';
import { colors, sizes } from 'src/themes/default';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { ProductEditKeysType } from './constants';
import api from '@api';
import Box from '@mui/material/Box';
import Tooltip from '../../components/Tooltip';
import { InfoIcon } from '../../components/SkladonIcon/Icons';
import { styled } from '@mui/material/styles';
import { FormErrorsType } from '../../types/global';

const initData: OrderProductType = {
    LINE_STATE: 'E'
};
type NewProductBlockPropsType = {
    data?: OrderProductType[];
    onChange: (value: any) => void;
    disableEdit?: boolean;
    orderProductCustomsTypeCodes: ConstantsCodeType[];
    editErrors: FormErrorsType;
};

const getProducts = async (sku: string): Promise<number | null> => {
    try {
        const url = `api/secure/products/products-availability`;
        const res = await api<{
            products: [{ AVAILABLE: number }]
        }>('post', url, { products: [{ SKU: sku }] });

        if (res.data.products.length) {
            return res.data.products[0].AVAILABLE || null;
        } else {
            return null;
        }
    }
    catch (e) {
        return null;
    }
};

const InfoIconStyled = styled('span')({
    marginLeft: '5px',

    '& svg': {
        width: '14px',
        height: '14px',
        marginRight: '10px'
    }
});

const NewProductBlock: FC<NewProductBlockPropsType> = (props) => {
    const formRef = useRef<HTMLFormElement>(null);

    const [editData, setEditData] = useState<OrderProductType>(initData);
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [availability, setAvailability] = useState<number | null | undefined>(undefined);
    const [loadingAvailability, setLoadingAvailability] = useState<boolean>(false);
    const changeValue = (key: ProductEditKeysType, value: any, error: boolean) => {
        const newData = { ...editData };
        if (key === 'PRODUCT_ID') {
            newData.PRODUCT_ID = value?.ID;
            newData.PRODUCT = value;
        } else {
            newData[key] = value;
        }
        const newErrors = { ...editErrors };
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleAddRow = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            const newId =
                Math.min.apply(0, [0, ...(props.data || []).map((item: any): any => item.ORDERS_PRODUCTS_ID)]) - 1;

            props.onChange([
                ...(props.data || []),
                {
                    ...editData,
                    ORDERS_PRODUCTS_ID: newId
                }
            ]);

            setEditData(initData);
            setAvailability(undefined);
        }

        setEditErrors(newFormErrors);
    };

    return (
        <>
            <Typography gutterBottom variant="h3"
                        sx={props.editErrors.ORDER_PRODUCTS ? { color: colors.alert_O } : {}}>
                {translate('orderDetail.addProduct')}
            </Typography>
            <form ref={formRef}>
                <Grid>
                    <GridItem cols={4}>
                        <ProductsSelect
                            value={editData.PRODUCT_ID}
                            error={editErrors.PRODUCT_ID}
                            idKey="ID"
                            onChange={async (value, error) => {
                                setLoadingAvailability(true);
                                changeValue('PRODUCT_ID', value, error);
                                const quantity = await getProducts(Array.isArray(value) ? value[0].SKU : value.SKU);
                                setAvailability(quantity);
                                setLoadingAvailability(false);
                            }}
                            name="PRODUCT_ID"
                            label={translate('orderDetail.productTable.name')}
                            extendsData={['PRODUCT_TYPE', 'INSERT_TYPE']}
                            fullWidth
                            disabled={props.disableEdit}
                            required
                        />
                    </GridItem>
                    <GridItem cols={2}>
                        <Box sx={{ width: '150px' }}>
                            <Typography
                                variant="caption"
                                component="label"
                                sx={{
                                    fontSize: '14px',
                                    lineHeight: '18px',
                                    fontWeight: 400,
                                    color: '#7E8CB2',
                                    WebkitTransform: 'unset',
                                    MozTransform: 'unset',
                                    msTransform: 'unset',
                                    transform: 'unset',
                                    WebkitTransition: 'unset',
                                    transition: 'unset',
                                    backgroundColor: 'transparent'
                                }}
                            >
                                {translate('orderDetail.productTable.available')}
                                <Tooltip title={translate('orderDetail.productTable.available.tooltip')}>
                                    <InfoIconStyled>
                                        <InfoIcon />
                                    </InfoIconStyled>
                                </Tooltip>
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    marginTop: '9px',
                                    verticalAlign: 'center',
                                    color: availability && availability < 0 ? colors.alert_O : colors.text_T1,
                                    fontSize: '16px',
                                    textTransform: 'uppercase'
                                }}
                            >
                                {availability === undefined ? null : availability || 0}
                            </Typography>
                        </Box>
                    </GridItem>
                    <GridItem cols={2}>
                        <TextField
                            value={editData.COUNT}
                            error={editErrors.COUNT}
                            onChange={(value, error) => changeValue('COUNT', value, error)}
                            name="COUNT"
                            label={translate('orderDetail.productTable.count')}
                            fullWidth
                            required
                            type="numberLocale"
                            min={1}
                            step="1"
                            disabled={props.disableEdit}
                        />
                    </GridItem>
                    <GridItem cols={2}>
                        <TextField
                            value={editData.PRODUCT_PRICE}
                            error={editErrors.PRODUCT_PRICE}
                            onChange={(value, error) => changeValue('PRODUCT_PRICE', value, error)}
                            name="PRODUCT_PRICE"
                            label={translate('orderDetail.productTable.price')}
                            fullWidth
                            required
                            type="numberLocale"
                            sx={{ fontWeight: 'bold' }}
                            min={0}
                            disabled={props.disableEdit}
                        />
                    </GridItem>
                    <GridItem cols={2}>
                        <Button
                            onClick={handleAddRow}
                            disabled={props.disableEdit || loadingAvailability}
                            sx={{ marginTop: sizes.input_top_PD, fontWeight: 'normal' }}
                            mobileFullWidht
                        >
                            + {translate('orderDetail.productTable.action.add')}
                        </Button>
                    </GridItem>
                </Grid>
            </form>
        </>
    );
};

export default NewProductBlock;
